var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { MultisigModule } from '@background/multisig.module';
import AccountUtils from '@popup/steem/utils/account.utils';
import { ErrorUtils } from '@popup/steem/utils/error.utils';
import { KeysUtils } from '@popup/steem/utils/keys.utils';
import MkUtils from '@popup/steem/utils/mk.utils';
import { MultisigUtils } from '@popup/steem/utils/multisig.utils';
import { BackgroundCommand } from '@reference-data/background-message-key.enum';
import { DefaultRpcs } from '@reference-data/default-rpc.list';
import { sleep } from '@steempro/dsteem/lib/utils';
import { PrivateKey, Transaction as SteemTransaction, config as SteemTxConfig, call, } from '@steempro/steem-tx-js';
import Config from 'src/config';
import { KeychainError } from 'src/keychain-error';
import Logger from 'src/utils/logger.utils';
const MINUTE = 60;
// default values that are already defined in config.js
const setRpc = (rpc) => __awaiter(void 0, void 0, void 0, function* () {
    SteemTxConfig.node = rpc.uri === 'DEFAULT' ? DefaultRpcs[0].uri : rpc.uri;
    if (rpc.chainId) {
        SteemTxConfig.chain_id = rpc.chainId;
    }
});
const sendOperation = (operations, key, confirmation, options) => __awaiter(void 0, void 0, void 0, function* () {
    operations.forEach((operation) => {
        var _a;
        const expiration = (_a = operation[1]) === null || _a === void 0 ? void 0 : _a.expiration;
        if (expiration && typeof expiration === 'number') {
            operation[1].expiration = new Date(expiration * 1000)
                .toISOString()
                .split('.')[0];
        }
    });
    const transactionResult = yield SteemTxUtils.createSignAndBroadcastTransaction(operations, key, options);
    if (transactionResult) {
        if (transactionResult === null || transactionResult === void 0 ? void 0 : transactionResult.isUsingMultisig) {
            return {
                id: transactionResult.tx_id,
                tx_id: transactionResult.tx_id,
                isUsingMultisig: true,
            };
        }
        else {
            return {
                id: transactionResult.tx_id,
                tx_id: transactionResult.tx_id,
                confirmed: confirmation
                    ? yield confirmTransaction(transactionResult.tx_id)
                    : false,
            };
        }
    }
    else {
        return null;
    }
});
const createTransaction = (operations) => __awaiter(void 0, void 0, void 0, function* () {
    let steemTransaction = new SteemTransaction();
    const tx = yield steemTransaction.create(operations, Config.transactions.expirationTimeInMinutes * MINUTE);
    Logger.log(`length of transaction => ${JSON.stringify(tx).length}`);
    return tx;
});
const createSignAndBroadcastTransaction = (operations, key, options) => __awaiter(void 0, void 0, void 0, function* () {
    let steemTransaction = new SteemTransaction();
    let transaction = yield steemTransaction.create(operations, Config.transactions.expirationTimeInMinutes * MINUTE);
    const username = MultisigUtils.getUsernameFromTransaction(transaction);
    const transactionAccount = yield AccountUtils.getExtendedAccount(username.toString());
    const localAccounts = yield AccountUtils.getAccountsFromLocalStorage(yield MkUtils.getMkFromLocalStorage());
    const localAccount = localAccounts.find((account) => account.keys.posting === key || account.keys.active === key);
    const initiatorAccount = yield AccountUtils.getExtendedAccount(localAccount === null || localAccount === void 0 ? void 0 : localAccount.name);
    const method = yield KeysUtils.isKeyActiveOrPosting(key, initiatorAccount);
    const isUsingMultisig = KeysUtils.isUsingMultisig(key, transactionAccount, initiatorAccount.name, method.toLowerCase());
    if (isUsingMultisig) {
        transaction = yield steemTransaction.create(operations, Config.transactions.multisigExpirationTimeInMinutes * MINUTE);
        const signedTransaction = yield signTransaction(transaction, key);
        if (!signedTransaction) {
            throw new Error('html_popup_error_while_signing_transaction');
        }
        let response;
        try {
            if (document) {
                response = yield useMultisig(transaction, key, initiatorAccount, transactionAccount, method, signedTransaction === null || signedTransaction === void 0 ? void 0 : signedTransaction.signatures[0], options);
                return {
                    status: response,
                    tx_id: '',
                    isUsingMultisig: true,
                };
            }
        }
        catch (err) {
            response = yield useMultisigThroughBackgroundOnly(transaction, key, initiatorAccount, transactionAccount, method, signedTransaction === null || signedTransaction === void 0 ? void 0 : signedTransaction.signatures[0], options);
            if (response.error) {
                throw new KeychainError(response.error.message);
            }
            else {
                return {
                    status: 'ok',
                    tx_id: response,
                    isUsingMultisig: true,
                };
            }
        }
    }
    else {
        try {
            const privateKey = PrivateKey.fromString(key.toString());
            steemTransaction.sign(privateKey);
        }
        catch (err) {
            Logger.error(err);
            throw new Error('html_popup_error_while_signing_transaction');
        }
    }
    let response;
    try {
        response = yield steemTransaction.broadcast();
        if (response.result) {
            const result = response.result;
            return Object.assign({}, result);
        }
    }
    catch (err) {
        Logger.error(err);
        throw new Error('html_popup_error_while_broadcasting');
    }
    response = response;
    if (response.error) {
        Logger.error('Error during broadcast', response.error);
        throw ErrorUtils.parse(response.error);
    }
});
/* istanbul ignore next */
const confirmTransaction = (transactionId) => __awaiter(void 0, void 0, void 0, function* () {
    if (transactionId) {
        Logger.info('Transaction confirmed');
        return true;
    }
    else {
        Logger.error(`Transaction failed`);
        return false;
    }
});
const signTransaction = (tx, key) => __awaiter(void 0, void 0, void 0, function* () {
    const hiveTransaction = new SteemTransaction(tx);
    try {
        const privateKey = PrivateKey.fromString(key.toString());
        return hiveTransaction.sign(privateKey);
    }
    catch (err) {
        Logger.error(err);
        throw new KeychainError('html_popup_error_while_signing_transaction');
    }
});
const broadcastAndConfirmTransactionWithSignature = (transaction, signature, confirmation) => __awaiter(void 0, void 0, void 0, function* () {
    let hiveTransaction = new SteemTransaction(transaction);
    if (typeof signature === 'string') {
        hiveTransaction.addSignature(signature);
    }
    else {
        for (const si of signature) {
            hiveTransaction.addSignature(si);
        }
    }
    let response;
    try {
        Logger.log(hiveTransaction);
        response = yield hiveTransaction.broadcast();
        if (response.result) {
            const transactionResult = response.result;
            return {
                id: transactionResult.tx_id,
                tx_id: transactionResult.tx_id,
                confirmed: confirmation
                    ? yield confirmTransaction(transactionResult.tx_id)
                    : false,
            };
        }
    }
    catch (err) {
        Logger.error(err);
        throw new Error('html_popup_error_while_broadcasting');
    }
    response = response;
    if (response.error) {
        Logger.error('Error during broadcast', response.error);
        throw ErrorUtils.parse(response.error);
    }
});
/* istanbul ignore next */
const getData = (method, params, key) => __awaiter(void 0, void 0, void 0, function* () {
    const response = yield call(method, params, 3000);
    if (response === null || response === void 0 ? void 0 : response.result) {
        return key ? response.result[key] : response.result;
    }
    else {
        if (window && window.document) {
            import('src/utils/rpc-switcher.utils').then(({ useWorkingRPC }) => {
                useWorkingRPC();
            });
        }
        throw new Error(`Error while retrieving data from ${method} : ${JSON.stringify(response.error)}`);
    }
});
const useMultisigThroughBackgroundOnly = (transaction, key, initiatorAccount, transactionAccount, method, signature, options) => __awaiter(void 0, void 0, void 0, function* () {
    return MultisigModule.requestSignatures({
        transaction: transaction,
        key: key,
        initiatorAccount: initiatorAccount,
        transactionAccount: transactionAccount,
        method: method,
        signature: signature,
        options: options,
    }, false);
});
const useMultisig = (transaction, key, initiatorAccount, transactionAccount, method, signature, options) => __awaiter(void 0, void 0, void 0, function* () {
    return new Promise((resolve, reject) => {
        const handleResponseFromBackground = (backgroundMessage, sender, sendResp) => {
            if (backgroundMessage.command ===
                BackgroundCommand.MULTISIG_REQUEST_SIGNATURES_RESPONSE) {
                chrome.runtime.onMessage.removeListener(handleResponseFromBackground);
                resolve(backgroundMessage.value);
            }
        };
        chrome.runtime.onMessage.addListener(handleResponseFromBackground);
        chrome.runtime.sendMessage({
            command: BackgroundCommand.MULTISIG_REQUEST_SIGNATURES,
            value: {
                transaction: transaction,
                key: key,
                initiatorAccount: initiatorAccount,
                transactionAccount: transactionAccount,
                method: method,
                signature: signature,
                options: options,
            },
        });
    });
});
const getTransaction = (txId) => __awaiter(void 0, void 0, void 0, function* () {
    yield sleep(3000);
    return SteemTxUtils.getData('condenser_api.get_transaction', [txId]);
});
export const SteemTxUtils = {
    getTransaction,
    sendOperation,
    createSignAndBroadcastTransaction,
    // confirmTransaction,
    getData,
    setRpc,
    createTransaction,
    signTransaction,
    broadcastAndConfirmTransactionWithSignature,
};
